import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, X, Loader } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

/**
 * MediaSelector Component
 * Modal dialog for uploading and selecting images from media library
 */
export const MediaSelector = ({ isOpen, onClose, onSelect, currentUrl = '' }) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [altText, setAltText] = useState('');

  // Fetch media list
  useEffect(() => {
    if (isOpen) {
      fetchMedia();
    }
  }, [isOpen]);

  const fetchMedia = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin/media', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMedia(response.data || []);
    } catch (error) {
      console.error('Failed to fetch media:', error);
      toast.error('Erreur lors du chargement des médias');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/pjpeg', 'image/png', 'image/x-png', 'image/gif', 'image/webp', ''];
      const extension = (file.name.split('.').pop() || '').toLowerCase();
      const validExtensions = ['jpg', 'jpeg', 'jpe', 'jfif', 'png', 'gif', 'webp'];
      if (!validTypes.includes(file.type) && !validExtensions.includes(extension)) {
        toast.error('Type de fichier non supporté. Formats acceptés: JPEG, PNG, GIF, WebP');
        return;
      }
      // Validate file size (50MB max)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Fichier trop volumineux. Taille maximum: 50MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Sélectionnez une image à uploader');
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('alt_text', altText);

      const response = await axios.post('/api/admin/media/upload', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Image uploadée avec succès');
      setSelectedFile(null);
      setAltText('');
      await fetchMedia();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleSelectMedia = (mediaItem) => {
    onSelect(mediaItem);
    onClose();
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette image ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/admin/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Image supprimée');
      await fetchMedia();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Médiathèque - Sélectionner une image</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Upload Section */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 bg-gray-50">
            <h3 className="font-semibold text-lg mb-4">Uploader une nouvelle image</h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-4">
                <input
                  type="file"
                  id="media-input"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <label htmlFor="media-input" className="cursor-pointer">
                  <div className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50">
                    <Upload size={18} />
                    <span>Choisir une image</span>
                  </div>
                </label>
                
                {selectedFile && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="truncate max-w-xs">{selectedFile.name}</span>
                    <span className="text-gray-400">({(selectedFile.size / 1024 / 1024).toFixed(2)}MB)</span>
                    <button
                      onClick={() => setSelectedFile(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>

              {selectedFile && (
                <>
                  <input
                    type="text"
                    placeholder="Texte alternatif (optionnel)"
                    value={altText}
                    onChange={(e) => setAltText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                  <Button
                    onClick={handleUpload}
                    disabled={uploading}
                    className="w-full"
                  >
                    {uploading ? (
                      <>
                        <Loader size={18} className="animate-spin mr-2" />
                        Upload en cours...
                      </>
                    ) : (
                      'Uploader l\'image'
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Media Library */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Bibliothèque de médias</h3>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader className="animate-spin" />
              </div>
            ) : media.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {media.map((item) => (
                  <div
                    key={item.id}
                    className={`relative group border rounded-lg overflow-hidden cursor-pointer transition ${
                      currentUrl === item.url
                        ? 'ring-2 ring-blue-500'
                        : 'hover:ring-2 hover:ring-gray-300'
                    }`}
                  >
                    <img
                      src={item.url}
                      alt={item.alt_text || item.filename}
                      className="w-full h-40 object-cover"
                    />
                    
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                      <Button
                        size="sm"
                        onClick={() => handleSelectMedia(item)}
                        className="bg-blue-500 hover:bg-blue-600"
                      >
                        Sélectionner
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteMedia(item.id);
                        }}
                      >
                        <X size={16} />
                      </Button>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-xs opacity-0 group-hover:opacity-100 transition">
                      <div className="truncate">{item.filename}</div>
                      {item.width && item.height && (
                        <div>{item.width}x{item.height}px</div>
                      )}
                      <div>{(item.size / 1024).toFixed(1)}KB</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>Aucune image dans la médiathèque</p>
                <p className="text-sm">Uploadez votre première image ci-dessus</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
